/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */

import {computed, ref} from 'vue';
import {APIService} from '@/core/util/services/api.service';

export const RTT_LEAVE_TYPE_NAME = 'Jour de Repos Supplémentaire';

export const STRUCTURAL_DAY_TYPE_KEYS = [
  'jour_normal',
  'week_end',
  'jour_ferie',
  'non_eligible',
] as const;

export type StructuralDayTypeKey = (typeof STRUCTURAL_DAY_TYPE_KEYS)[number];

export const DECLARATION_DAY_TYPE_KEYS = ['maladie'] as const;

export type DeclarationDayTypeKey = (typeof DECLARATION_DAY_TYPE_KEYS)[number];

export type DayTypeKey = string;

export type DayBucket =
  | 'non_eligible'
  | 'non_working'
  | 'working_always'
  | 'leave_type'
  | 'declaration_type';

export type ForfaitJoursDay = {
  day: number;
  dayTypeKey: DayTypeKey;
  source: 'auto' | 'user';
  halfDay: boolean;
  restRespected: boolean;
  comment: string | null;
  nonEligible?: boolean;
  nonWorking?: boolean;
};

export type ForfaitJoursDeclaration = {
  id: number;
  empNumber: number;
  year: number;
  month: number;
  status: 'DRAFT' | 'SUBMITTED' | 'SIGNED' | 'CLOSED';
  signedEmployeeName: string | null;
  signedAt: string | null;
  editable: boolean;
  signatureAcknowledged: boolean;
  days: ForfaitJoursDay[];
  nextTransitionDate?: string | null;
  daysUntilTransition?: number | null;
};

export type UseForfaitJoursOptions = {
  debounceMs?: number;
};

export type SetDayField = (
  day: number,
  field: 'dayTypeKey' | 'halfDay' | 'restRespected' | 'comment',
  value: string | number | boolean | null,
) => void;

/**
 * Day-type key categorisation helpers.
 *
 * The Forfait Jours declaration grid distinguishes 4 structural day
 * types (`jour_normal`, `week_end`, `jour_ferie`, `non_eligible`)
 * from active `LeaveType` keys (e.g., `Congé Payé`). The RTT leave
 * type (`Jour de Repos Supplémentaire`) is a `LeaveType` key but is
 * bucketed as working-always (DECL-08). The declaration-only types
 * (e.g. `maladie`) are kept separate from leave types and do not
 * count as worked days.
 */
export function isStructuralKey(key: DayTypeKey): boolean {
  return (STRUCTURAL_DAY_TYPE_KEYS as readonly string[]).includes(key);
}

export function isDeclarationDayTypeKey(key: DayTypeKey): boolean {
  return (DECLARATION_DAY_TYPE_KEYS as readonly string[]).includes(key);
}

export function isNonEligibleKey(key: DayTypeKey): boolean {
  return key === 'non_eligible';
}

export function isNonWorkingKey(key: DayTypeKey): boolean {
  return key === 'week_end' || key === 'jour_ferie';
}

export function isLeaveTypeKey(key: DayTypeKey): boolean {
  return !isStructuralKey(key) && !isDeclarationDayTypeKey(key);
}

export function categorizeDayTypeKey(key: DayTypeKey): DayBucket {
  if (isNonEligibleKey(key)) {
    return 'non_eligible';
  }
  if (isNonWorkingKey(key)) {
    return 'non_working';
  }
  if (key === RTT_LEAVE_TYPE_NAME || isStructuralKey(key)) {
    return 'working_always';
  }
  if (isDeclarationDayTypeKey(key)) {
    return 'declaration_type';
  }
  return 'leave_type';
}

/**
 * Working-day rule (DECL-08). A day is a working day if it is
 * `jour_normal` or `rtt`, or a leave-type day marked as
 * half-day. The RTT leave type (`Jour de Repos Supplémentaire`)
 * behaves like the structural `rtt` key: it always counts as a
 * working day. Declaration-only types such as `maladie`, along
 * with `non_eligible`, `week_end`, and `jour_ferie`, are never
 * working days.
 */
export function isWorkingDay(
  day: Pick<ForfaitJoursDay, 'dayTypeKey' | 'halfDay'>,
): boolean {
  const bucket = categorizeDayTypeKey(day.dayTypeKey);
  if (bucket === 'working_always') {
    return true;
  }
  if (bucket === 'leave_type') {
    return day.halfDay === true;
  }
  return false;
}

const DEFAULTS: UseForfaitJoursOptions = {
  debounceMs: 800,
};

function indexByDay(days: ForfaitJoursDay[]): Map<number, ForfaitJoursDay> {
  const map = new Map<number, ForfaitJoursDay>();
  for (const day of days) {
    map.set(day.day, {...day});
  }
  return map;
}

function diff(
  initial: Map<number, ForfaitJoursDay>,
  current: Map<number, ForfaitJoursDay>,
): ForfaitJoursDay[] {
  const out: ForfaitJoursDay[] = [];
  for (const [day, currentDay] of current.entries()) {
    const initialDay = initial.get(day);
    if (!initialDay) {
      continue;
    }
    if (
      initialDay.dayTypeKey !== currentDay.dayTypeKey ||
      initialDay.halfDay !== currentDay.halfDay ||
      initialDay.restRespected !== currentDay.restRespected ||
      initialDay.comment !== currentDay.comment
    ) {
      out.push(currentDay);
    }
  }
  return out;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export default function useForfaitJours(
  declarationId: number,
  options: UseForfaitJoursOptions = {},
) {
  const {debounceMs} = {...DEFAULTS, ...options};

  const http = new APIService(
    // @ts-expect-error: appGlobal is not in window object by default
    window.appGlobal.baseUrl,
    '/api/v2/forfait-jours/my-declarations',
  );

  const declaration = ref<ForfaitJoursDeclaration | null>(null);
  const daysById = ref<Map<number, ForfaitJoursDay>>(new Map());
  const isLoading = ref(false);
  const isSaving = ref(false);
  const isSubmitting = ref(false);
  const error = ref<string | null>(null);
  const modifiedDays = ref<Set<number>>(new Set());
  const initialDays = ref<Map<number, ForfaitJoursDay>>(new Map());
  const signatureAcknowledged = ref<boolean>(false);

  let debounceHandle: ReturnType<typeof setTimeout> | null = null;

  function applyDeclaration(payload: ForfaitJoursDeclaration): void {
    const next = {
      ...payload,
      days: payload.days.map((d) => ({...d})),
    };
    declaration.value = next;
    daysById.value = indexByDay(next.days);
    initialDays.value = indexByDay(next.days);
    modifiedDays.value = new Set();
    signatureAcknowledged.value = payload.signatureAcknowledged === true;
  }

  async function load(): Promise<void> {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await http.get(declarationId);
      const payload = response?.data?.data;
      if (!isPlainObject(payload)) {
        throw new Error('Malformed declaration payload from server.');
      }
      applyDeclaration(payload as unknown as ForfaitJoursDeclaration);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      error.value = message;
    } finally {
      isLoading.value = false;
    }
  }

  const setDayField: SetDayField = (day, field, value) => {
    if (!declaration.value) {
      return;
    }
    const current = daysById.value.get(day);
    if (!current || current.nonEligible) {
      return;
    }
    if (!declaration.value.editable) {
      return;
    }
    const next: ForfaitJoursDay = {
      ...current,
      [field]: value,
      source: 'user',
    };
    daysById.value.set(day, next);
    modifiedDays.value.add(day);
    scheduleSave();
  };

  function scheduleSave(): void {
    if (debounceHandle !== null) {
      clearTimeout(debounceHandle);
    }
    debounceHandle = setTimeout(() => {
      debounceHandle = null;
      void persistDraft();
    }, debounceMs);
  }

  function buildPatchPayload(): {
    days: Partial<ForfaitJoursDay>[];
  } | null {
    if (!declaration.value) {
      return null;
    }
    const days = diff(initialDays.value, daysById.value).map((d) => ({
      day: d.day,
      dayTypeKey: d.dayTypeKey,
      halfDay: d.halfDay,
      restRespected: d.restRespected,
      comment: d.comment,
      source: d.source,
    }));
    if (days.length === 0) {
      return null;
    }
    return {days};
  }

  async function persistDraft(): Promise<void> {
    if (isSaving.value) {
      return;
    }
    if (!declaration.value || !declaration.value.editable) {
      return;
    }
    const payload = buildPatchPayload();
    if (!payload) {
      return;
    }
    isSaving.value = true;
    try {
      const response = await http.update(declarationId, payload);
      const next = response?.data?.data;
      if (isPlainObject(next)) {
        applyDeclaration(next as unknown as ForfaitJoursDeclaration);
      } else {
        // Reapply the current local state as the new baseline so the
        // next diff is computed against the just-persisted snapshot.
        initialDays.value = new Map(daysById.value);
        modifiedDays.value = new Set();
      }
      error.value = null;
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      error.value = message;
    } finally {
      isSaving.value = false;
    }
  }

  async function flushSave(): Promise<void> {
    if (debounceHandle !== null) {
      clearTimeout(debounceHandle);
      debounceHandle = null;
    }
    await persistDraft();
  }

  const canSubmit = computed<boolean>(() => {
    if (!declaration.value) {
      return false;
    }
    if (!declaration.value.editable) {
      return false;
    }
    if (!signatureAcknowledged.value) {
      return false;
    }
    return true;
  });

  async function submit(): Promise<void> {
    if (!declaration.value) {
      return;
    }
    if (!canSubmit.value) {
      return;
    }
    if (isSubmitting.value) {
      return;
    }
    await flushSave();
    isSubmitting.value = true;
    try {
      const response = await http.request({
        method: 'PUT',
        url: `/api/v2/forfait-jours/my-declarations/${declarationId}/submit`,
        data: {signatureAcknowledged: signatureAcknowledged.value},
      });
      const next = response?.data?.data;
      if (isPlainObject(next)) {
        applyDeclaration(next as unknown as ForfaitJoursDeclaration);
      }
      error.value = null;
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      error.value = message;
    } finally {
      isSubmitting.value = false;
    }
  }

  return {
    http,
    declaration,
    daysById,
    isLoading,
    isSaving,
    isSubmitting,
    error,
    modifiedDays,
    canSubmit,
    signatureAcknowledged,
    load,
    setDayField,
    scheduleSave,
    persistDraft,
    flushSave,
    submit,
  };
}

// Static helpers exposed for unit-testing pure logic and for reuse
// in the Vue components (kept off the runtime `useForfaitJours()`
// call so consumers import them as plain functions).
useForfaitJours.isDayModified = (
  day: Pick<ForfaitJoursDay, 'source'>,
): boolean => day.source === 'user';

useForfaitJours.isDayLocked = (
  day: Pick<ForfaitJoursDay, 'dayTypeKey' | 'source'>,
  declarationEditable: boolean,
): boolean => day.dayTypeKey === 'non_eligible' || !declarationEditable;
